import React, {
  useState,
  useEffect
} from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link as RouterLink,
} from "react-router-dom";

import moment from 'moment';


import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  Typography,
  Button,
  Divider,
  Collapse,
  ListSubheader,
} from '@material-ui/core';
import {
  Skeleton
} from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';

import localization from '../utils/localizations';
import {
  IssueReportPanel,
  ThesisUGBDetail,
  ThesisUGBForm,
  StatusTimeline,
  CurrentStatus,
  DateTimeView,
  FileCommentsHistory,
  AdminNotes,
} from '../views';

import { useStatus } from '../data/Status';
import filterDocumentBy from '../utils/filterDocumentBy';
import grabAllDocuments from '../utils/grabAllDocuments';
import {ThesisUGBGradingReviewViewer} from '../views/thesis';

import {
  Document,
  ReviewAssignment,
  Comment,
} from '../types';

const appConfig = (window as any).APP_CONFIG;


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  secondPanel: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  pageTitle: {
    marginTop: 10,
    marginBottom: 10,
  },
  breadcrumbs: {
    margin: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  gridLabel: {
    textAlign: 'right',
    color: '#555555',
  },
  alignRight: {
    textAlign: 'right',
  },
  ugbOpenMessage: {
    marginTop: 20,
    marginBottom: 20,
  }
}));


function ThesisUGBPage() {
  const classes = useStyles();

  const [status, statusLoading, loadStatus] = useStatus();
  const [showUGBForm, setShowUGBForm] = useState(false);
  let allDocuments: Document[] = [];
  const reviewerComments: Comment[] = [];

  let currentUGBData = undefined;
  let editButtonLabel = `${localization.submit} ${localization.ugb}`;
  if (status && status.ugb && status.ugb.id) {
    currentUGBData = {
      title: status.ugb.title,
      s1_background_university: status.ugb.s1_background_university,
      s1_background_program: status.ugb.s1_background_program,
      s1_fast_track: status.ugb.s1_fast_track,
      s1_final_project_title: status.ugb.s1_final_project_title,
      s1_final_project_advisor: status.ugb.s1_final_project_advisor,
      s1_publication: status.ugb.s1_publication,
      s1_publication_journal_publisher: status.ugb.s1_publication_journal_publisher,
      s1_publication_author: status.ugb.s1_publication_author,
      s1_publication_title: status.ugb.s1_publication_title,
      primary_advisor: status.ugb.primary_advisor?.id,
      additional_advisors: status.ugb.additional_advisors.map((advisor: {id: string}) => advisor.id),
      advisor_research: status.ugb.advisor_research,
      ktm: filterDocumentBy(status.ugb.documents, {kind: 'KTM'}),
      ksm: filterDocumentBy(status.ugb.documents, {kind: 'KSM'}),
      transcript: filterDocumentBy(status.ugb.documents, {kind: 'Transkrip'}),
      ugb_docx: filterDocumentBy(status.ugb.documents, {kind: 'UGB', content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}),
      ugb_pdf: filterDocumentBy(status.ugb.documents, {kind: 'UGB', content_type: "application/pdf"}),
    }
    if ((status.ugb.status == 'approved') || (status.ugb.status == 'approved_with_revision') || (status.ugb.status == 'presentation_required') || (status.ugb.status == 'submitted') || (status.ugb.status == 'closed') || (status.ugb.status == 'revision_required')) {
      editButtonLabel = localization.updateData;
    }
    
    allDocuments = grabAllDocuments(status.ugb.documents, status.ugb.status_history);

    if (status.ugb.review_assignments) {
      status.ugb.review_assignments.forEach((assignment: ReviewAssignment) => {
        assignment.status_history.forEach((history) => {
          if (history.comment) reviewerComments.push(history.comment);
        })
      })
    }

  }

  let disableEdit = !(status?.ugb);
  let submissionDeadlineReached = false;
  const period = status.current_ugb_period;
  let deadline = period?.end;

  if (period?.end) {
    if (moment() > moment(deadline)) {
      disableEdit = true;
      submissionDeadlineReached = true;
    }
  }

  if (status.student && status.student.can_submit_ugb === false) {
    if (status && status.ugb && !status.ugb.id) {
      disableEdit = true;
    }
  }

  if (status && status.ugb && status.ugb.id) {
    if ((status.ugb.status == 'accepted') || (status.ugb.status == 'approved') || (status.ugb.status == 'closed')) {
      disableEdit = true;
    }
  }

  return (
    <>
    <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
      <Link color="inherit" to={`${appConfig.homepage}`} component={RouterLink}>
        {localization.home}
      </Link>
      <Typography color="textPrimary">{localization.ugb}</Typography>
    </Breadcrumbs>

    <Paper className={classes.mainBody}>
      <ListSubheader style={{paddingLeft: 8}}>{localization.ugbPageTitle}</ListSubheader>

      <Collapse
        in={!showUGBForm}
      >
        {!status.success ? (
          <div style={{position: 'relative', padding: 10}}>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 10}}/>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 40}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
          </div>
        ) : (
          submissionDeadlineReached ?
          (<Alert
            severity="error"
            className={classes.ugbOpenMessage}
          >
            {localization.submissionHasBeenClosedOn} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert>)
          :
          (period && <Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.ugbSubmissionOpenUntil} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> ({localization.ending} <DateTimeView value={deadline} fromNow />)
          </Alert>)
        )}

        {status && status.ugb && status.ugb.status &&
          <CurrentStatus
            currentStatus={status.ugb.status}
            history={status.ugb.status_history}
            statusMap={[
              {
                status: 'draft',
                label: localization.submission,
                description: localization.awaitingSubmission,
                descriptionCompleted: localization.submissionSent,
              },
              {
                status: 'submitted',
                label: localization.review,
                description: localization.awaitingApproval,
                descriptionCompleted: localization.approved,
              },
              {
                status: 'approved',
                label: localization.approved,
                description: localization.ugbAccepted,
              },
            ]}
          />
        }

        {status && status.ugb && status.ugb.status && <ThesisUGBDetail ugb={status.ugb} /> }

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon/>}
              onClick={(e: any) => {
                setShowUGBForm(true);
              }}
              disabled={disableEdit}
            >{editButtonLabel}</Button>
          </Grid>
          <Grid item xs={6} className={classes.alignRight}>
            <Button
              variant="contained"
              to={`${appConfig.homepage}`} component={RouterLink}
            >{localization.backToHome}</Button>
          </Grid>
        </Grid>
      </Collapse>

      <Collapse
        in={showUGBForm}
        {...(showUGBForm ? { timeout: 1000 } : {})}
      >
        <ThesisUGBForm
          onSubmitted={() => {
            setShowUGBForm(false);
            loadStatus();
          }}
          onCancelled={() => {
            setShowUGBForm(false);
          }}
          values={currentUGBData}
        />
      </Collapse>
    </Paper>

    {(status?.ugb?.review_assignments) &&	status?.ugb?.status !== 'submitted' && 
      <div>
        {(status.ugb.review_assignments as ReviewAssignment[]).map((assignment, i) => {
          if (assignment.ugb_review) {
            return (
              <ThesisUGBGradingReviewViewer
                key={`review-${assignment.ugb_review}`}
                reviewId={assignment.ugb_review}
                reviewerName={`Reviewer ${i+1}`}
              />
            );
          }
          else return <></>
        })}
      </div>}

    {(status?.ugb?.notes?.length > 0) &&
      <AdminNotes notes={status.ugb.notes} />
    }

    {status && status.ugb && (allDocuments.length > 0) &&
      <FileCommentsHistory
        documents={allDocuments}
        reviewerComments={reviewerComments}
        collapsed={true}
        hideIfEmpty={true}
      />
    }

    {status && status.ugb && <StatusTimeline history={status.ugb.status_history} collapsed={true} hideIfEmpty={true}/> }

    <IssueReportPanel />
    </>
  );
}

export default ThesisUGBPage;